import React from 'react';
import { Text, Table, ActionIcon, Anchor, Image, Modal, ScrollArea, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { GoalBreakdownRows } from '../ReviewingTest/GoalBreakdownRows';

export function OptimizeReport({
  SelectedFilteredOptions,
  TestVariationsData,
  PauseFlaggedIds = [],
  AllGoalsMode = 0,
  GoalBreakdown = {},
  ExpandedVariations = [],
  OnToggleVariation = null,
  ShowPreviewLinks = false,
  EmptyMessage = '',
  }:any) {

  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';

  /* Preview: the variation's screenshot frozen into the history record when the test ended
     (element.preview from test_history_optimize_report). Nothing is captured from this page. */
  const [openedPreview, setOpenedPreview] = React.useState<any>(null);
  const previewCell = (element: any) => {
    const preview = element?.preview;
    if (preview && preview.url) {
      return (
        <img
          src={preview.url}
          alt={`Variation ${element.variation_number}`}
          onClick={() => setOpenedPreview(element)}
          style={{ width: 80, height: 50, objectFit: 'cover', objectPosition: 'top', border: '1px solid #dee2e6', borderRadius: 3, cursor: 'zoom-in', display: 'block' }}
        />
      );
    }
    return <Text fz={11} c="dimmed" title={preview?.reason || 'No screenshot was saved for this test.'}>No preview</Text>;
  };

  // Phase 5 — concluded-test display: 🟠 on variations that were Pause Recommended
  // at conclusion (preserved from the last day_key Phase 5 ran for this test).
  // Column is always rendered for visual consistency with the active-test variation
  // table (which always shows the P Status column). Conditionally hiding it caused
  // the column to "pop in" once the predictive-state API resolved — a layout shift
  // that looked like a bug. Always-on column shows dashes for non-flagged rows and
  // dashes for old concluded tests that never had Phase 5 data.
  const flaggedSet = new Set<number>((PauseFlaggedIds || []).map((v:any) => Number(v)));

  /* Customer Type rows for one variation, taken from the snapshot stored when the test was
     archived. Mapped into the same shape GoalBreakdownRows already renders, so labels, columns
     and formatting match the other reports - no Customer Type markup is added here, and the
     shared component is called, not modified. */
  const customerTypeRowsFor = (variationGoals: any[]) => {
    if (!Array.isArray(variationGoals)) return [];
    const out: any[] = [];
    variationGoals.forEach((goal: any) => {
      const ctRows = goal?.customer_type_rows;
      if (!Array.isArray(ctRows) || ctRows.length === 0) return;
      const uniq = Number(goal?.unique) || 0;
      ctRows.forEach((ct: any) => {
        const conv = Number(ct?.conversions) || 0;
        const rev = Number(ct?.revenue) || 0;
        out.push({
          goal_id: `${goal?.goal_id ?? ''}-${ct?.customer_type ?? ''}`,
          goal_name: `${goal?.goal_name ?? ''} - ${ct?.customer_type_name ?? ''}`,
          pageconvnum: conv,
          pageconv: uniq > 0 ? ((conv / uniq) * 100).toFixed(2) : '0.00',
          sub_revenue: (Number(ct?.sub_revenue) || 0).toFixed(2),
          revenue: rev.toFixed(2),
          aov: conv > 0 ? (rev / conv).toFixed(2) : '0.00',
          avg_revenue_per_unique: uniq > 0 ? (rev / uniq).toFixed(2) : '0.00',
        });
      });
    });
    return out;
  };

  /* Customer Type expands per variation, independently of the goal breakdown - the same
     separation Reviewing Test keeps between expandedVariations and expandedCustomerType.
     The rows are already in the response, so opening one costs no request. */
  const [expandedCustomerType, setExpandedCustomerType] = React.useState<string[]>([]);
  const toggleCustomerType = (variationNumber: any) => {
    const key = String(variationNumber);
    setExpandedCustomerType((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  /* The two segments are fixed, so the rows are drawn from the labels alone and a segment the
     archive did not record reads as zero instead of vanishing - the same idiom Reviewing Test
     uses. Single-goal mode has no goal breakdown, so the split arrives on the variation row
     itself (element.customer_type), the same key the live report uses. */
  const CT_SEGMENTS = ['First-time Customers', 'Returning Customers'];
  const customerTypeRowsForVariation = (element: any) => {
    const ctSupplied = Array.isArray(element?.customer_type) ? element.customer_type : [];
    const uniq = Number(element?.uniques) || 0;
    return CT_SEGMENTS.map((name) => {
      const ct   = ctSupplied.find((x: any) => x && x.customer_type_name === name) || {};
      const conv = Number(ct.conversions) || 0;
      const rev  = Number(ct.revenue) || 0;
      return {
        goal_name:   name,
        pageconvnum: conv,
        pageconv:    uniq > 0 ? ((conv / uniq) * 100).toFixed(2) : '0.00',
        revenue:     rev.toFixed(2),
        sub_revenue: (Number(ct.sub_revenue) || 0).toFixed(2),
        aov:         conv > 0 ? (rev / conv).toFixed(2) : '0.00',
        avg_revenue_per_unique: uniq > 0 ? (rev / uniq).toFixed(2) : '0.00',
        /* Improvement and significance are not reported per customer type - see the
           blankColumns passed to GoalBreakdownRows below. */
      };
    });
  };
  const isFlagged = (variationId:any) => flaggedSet.has(Number(variationId));
    const getBackgroundColor = (highlight) => {
      if (highlight === 'red') {
        return '#ffd9d9';
      }
      if (highlight === 'yellow') {
        return '#fff7c6';
      }
      return 'transparent';
    };

  const isAll = Number(AllGoalsMode) === 1;
  const variationGoalsFor = (variationNumber:any) => {
    const goals = GoalBreakdown?.[String(variationNumber)];
    return Array.isArray(goals) ? goals : [];
  };
  const goalCellDash = <Text fz={11} c="dimmed">—</Text>;

  const has = (m:string) => SelectedFilteredOptions.includes(m);
  const variationColumns: { key: string; label: string }[] = [
    { key: 'id',         label: 'ID' },
    { key: 'pstatus',    label: 'P Status' },
    { key: 'variations', label: 'Variations' },
    { key: 'preview',    label: 'Preview' },
  ];
  ([['Uniques','uniques'],['Visits','visits'],['Conv. # to page','conv_num'],
    ['Conv. % to page','conv_pct'],['Percentage Improvement','pi'],
    ['Statistical Significance','ss'],['Subscription Revenue','sub_rev'],
    ['Total Revenue','revenue'],['AOV','aov'],['EPC','epc'],
    ['Avg. Time Spent','time']] as [string,string][])
    .forEach(([label, key]) => { if (has(label)) variationColumns.push({ key, label }); });

  return (
    <>
      <Modal opened={openedPreview !== null} onClose={() => setOpenedPreview(null)} size="80%" trapFocus={false}
        title={openedPreview ? openedPreview.variations : ''}>
        {openedPreview && openedPreview.preview?.url && (
          <ScrollArea style={{ height: '75vh' }}>
            <Image src={openedPreview.preview.url} alt={openedPreview.variations} />
          </ScrollArea>
        )}
      </Modal>
      <Table striped highlightOnHover withBorder my={15} fontSize="xs">
        <thead>
          <tr>
            <th>ID</th>
            <th style={{ textAlign: 'center' }}>P Status</th>
            <th>Variations</th>
            <th>Preview</th>
             {SelectedFilteredOptions.includes('Uniques') && <th>Uniques</th>}
             {SelectedFilteredOptions.includes('Visits') && <th>Visits</th>}
             {SelectedFilteredOptions.includes('Conv. # to page') && <th>Conv. # to page</th>}
             {SelectedFilteredOptions.includes('Conv. % to page') && <th>Conv. % to page</th>}
             {SelectedFilteredOptions.includes('Percentage Improvement') &&<th>Percentage Improvement</th>}
             {SelectedFilteredOptions.includes('Statistical Significance') &&<th>Statistical Significance</th>}
             {SelectedFilteredOptions.includes('Subscription Revenue') && <th>Subscription Revenue</th>}
             {SelectedFilteredOptions.includes('Total Revenue') && <th>Total Revenue</th>}
             {SelectedFilteredOptions.includes('AOV') && <th>AOV</th>}
             {SelectedFilteredOptions.includes('EPC') && <th>EPC</th>}
             {SelectedFilteredOptions.includes('Avg. Time Spent') && <th>Avg. Time Spent</th>}
          </tr>
        </thead>
        <tbody>
            {/* Preview Stats opened before the snapshot was saved - say so inside the table. */}
            {EmptyMessage !== '' && TestVariationsData.length === 0 && (
              <tr>
                <td colSpan={variationColumns.length} style={{ textAlign: 'center' }}>
                  <Text fz={14} fw={500} py={10}>{EmptyMessage}</Text>
                </td>
              </tr>
            )}
            {TestVariationsData.map((element,index) => {
              const variationGoals = isAll ? variationGoalsFor(element.variation_number) : [];
              const ctKey = String(element.variation_number);
              const ctSupplied = Array.isArray(element?.customer_type) ? element.customer_type : [];
              /* Same rule as Reviewing Test: the split belongs to the single-goal view, and a
                 variation with no conversions has nothing to split, so no link is offered. */
              const ctEligible = !isAll && ctSupplied.length > 0 && Number(element.convert_to_page) > 0;
              const ctOpen = expandedCustomerType.includes(ctKey);
              const isExpanded = (ExpandedVariations || []).includes(Number(element.variation_number));
              return (
              <React.Fragment key={index}>
              <tr
                style={{
                        backgroundColor: getBackgroundColor(element.highlight_row),
                        color: element.highlight_row === 'yellow' && isDark ? 'black' : undefined,
                      }}
                onMouseEnter={(e) => {
                  if (element.highlight_row !== 'red') {
                    e.currentTarget.style.backgroundColor = '#fff7c6';
                    if (isDark) {
                      e.currentTarget.style.color = 'black';
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getBackgroundColor(element.highlight_row);
                  if (element.highlight_row === 'yellow' && isDark) {
                    e.currentTarget.style.color = 'black';
                  } else {
                    e.currentTarget.style.color = '';
                  }
                }}
              >
                <td>{element.variation_number}</td>
                <td style={{ textAlign: 'center' }}>
                  {isFlagged(element.variation_number) ? (
                    <Text fz={16} title="Pause Recommended (flagged at conclusion)">{'\u{1F7E0}'}</Text>
                  ) : (
                    <Text fz={10} c="dimmed">—</Text>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex'}}>
                    {isAll && variationGoals.length > 0 && (
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        mt={6}
                        mr={2}
                        aria-label={isExpanded ? 'Collapse goals' : 'Expand goals'}
                        title={isExpanded ? 'Collapse goals' : 'Expand goals'}
                        onClick={() => OnToggleVariation && OnToggleVariation(element.variation_number)}
                      >
                        {isExpanded ? <IconChevronDown size="0.9rem" /> : <IconChevronRight size="0.9rem" />}
                      </ActionIcon>
                    )}
                    {/* Preview Stats: the name opens the variation preview, same as Reviewing Test. */}
                    {ShowPreviewLinks && element.preview_adtest ? (
                      <Text mt={6} w={80}><Anchor href={element.preview_adtest} target="_blank" rel="noreferrer" color="inherit">{element.variations}</Anchor></Text>
                    ) : (
                      <Text mt={6} w={80}>{element.variations}</Text>
                    )}
                    {element.is_winner !== '' ? <Text mt={6} w={80} fz={12} weight={800} c="red">! winner</Text> : "" }
                  </div>
                  {ctEligible && (
                    <Anchor
                      component="button"
                      type="button"
                      fz={11}
                      fw={500}
                      underline={false}
                      onClick={() => toggleCustomerType(element.variation_number)}
                      aria-expanded={ctOpen}
                      style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2 }}
                    >
                      {ctOpen ? <IconChevronDown size="0.75rem" /> : <IconChevronRight size="0.75rem" />}
                      Customer Type
                    </Anchor>
                  )}
                </td>
                <td>{previewCell(element)}</td>
                {SelectedFilteredOptions.includes('Uniques') && <td>{element.uniques}</td>}
                {SelectedFilteredOptions.includes('Visits') && <td>{element.visits}</td>}
                {SelectedFilteredOptions.includes('Conv. # to page') && <td>{isAll ? goalCellDash : element.convert_to_page}</td>}
                {SelectedFilteredOptions.includes('Conv. % to page') && <td>{isAll ? goalCellDash : element.conversion_to_page_percent}</td>}
                {SelectedFilteredOptions.includes('Percentage Improvement') &&
                <td>
                  {isAll ? goalCellDash :
                   element.pi_per === 'Not Enough Data' || element.pi_per === '-' ? (
                    <Text c="dimmed" fw={500}>{element.pi_per}</Text>
                  ) : (
                    <div style={{ display: 'flex' }}>
                      <Text fw={500} w={40}>{Math.round(element.pi_per)}%</Text>
                      <div className="pi_graph pi_cf">
                        <div className={`pi_ext ${element.pi_class}`}>
                          <div style={{ width: `${Math.min(Math.round(element.pi_per_value), 100)}%` }} className={`pi_bar ${element.pi_class}`}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </td>
                }
                {SelectedFilteredOptions.includes('Statistical Significance') &&
                <>
                  {isAll ? (
                    <td>{goalCellDash}</td>
                  ) : (element.ss_class !== '' || element.ss_per !== 0) ? (
                  <td><div style={{ display: 'flex' }}><Text fw={500} style={{width:"5.5rem"}}>{element.ss_per}%</Text>{element.ss_per !== ''?<progress value={element.ss_per} style={{width:"208%",marginTop:"3px"}} className={element.ss_class} max="100"></progress>:''}</div></td>
                  ) : (
                    <td></td>
                  )}
                </>
                }
                {SelectedFilteredOptions.includes('Subscription Revenue') && <td>{isAll ? goalCellDash : element.sub_revenue}</td>}
                {SelectedFilteredOptions.includes('Total Revenue') && <td>{isAll ? goalCellDash : element.revenue}</td>}
                {SelectedFilteredOptions.includes('AOV') && <td>{isAll ? goalCellDash : element.aov}</td>}
                {SelectedFilteredOptions.includes('EPC') && <td>{isAll ? goalCellDash : element.arpu}</td>}
                {SelectedFilteredOptions.includes('Avg. Time Spent') && <td>{element.time_spent}</td>}
              </tr>
              {isAll && isExpanded && variationGoals.length > 0 && (
                <GoalBreakdownRows
                  goals={variationGoals}
                  columns={variationColumns}
                  isDark={isDark}
                  rowKeyPrefix={`v${element.variation_number}-`}
                />
              )}
              {/* Customer Type sits directly beneath the goals it splits, so it needs no second
                  header line; % Improvement and Significance do not apply to a split. */}
              {/* ctEligible as well as ctOpen: a variation left expanded whose conversions have
                  since gone to zero has no link to collapse it, so the block must close itself. */}
              {ctEligible && ctOpen && (
                <GoalBreakdownRows
                  goals={customerTypeRowsForVariation(element)}
                  columns={variationColumns}
                  isDark={isDark}
                  rowKeyPrefix={`ct${element.variation_number}-`}
                  blankColumns={['pi', 'ss']}
                  showHeading={false}
                />
              )}
              {isAll && isExpanded && customerTypeRowsFor(variationGoals).length > 0 && (
                <GoalBreakdownRows
                  goals={customerTypeRowsFor(variationGoals)}
                  columns={variationColumns}
                  isDark={isDark}
                  rowKeyPrefix={`v${element.variation_number}-ct-`}
                  showHeading={false}
                  blankColumns={['pi', 'ss']}
                />
              )}
              </React.Fragment>
              );
            })}
        </tbody>
      </Table>
    </>
  );
}